import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Box, FileText, Ship, ShieldCheck, Radar, PackageCheck, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "./primitives";

const nodes = [
  { icon: Box, label: "FullContainer™", accent: true },
  { icon: FileText, label: "Purchase Order" },
  { icon: Ship, label: "FreightIQ™" },
  { icon: ShieldCheck, label: "Inspection™" },
  { icon: Radar, label: "Live Shipment Tracking™" },
  { icon: PackageCheck, label: "Delivered", success: true },
];

export const IntegrationSection = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="integration"
      data-testid="integration-section"
      className="border-t border-slate-100 bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline>Workspace Integration</Overline>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
            Connected to the entire DeMaxtore ecosystem.
          </h2>
          <p className="mt-4 text-base text-slateink md:text-lg">
            FullContainer™ links every core module into one seamless operational flow.
          </p>
        </Reveal>

        <div className="mt-16 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center lg:justify-center">
          {nodes.map((n, i) => (
            <React.Fragment key={n.label}>
              <motion.div
                initial={reduce ? false : { opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`flex min-w-[150px] flex-1 flex-col items-center gap-3 rounded-2xl border p-5 text-center shadow-card-base ${
                  n.accent
                    ? "border-demax/30 bg-demax-soft"
                    : n.success
                    ? "border-[#A7F3D0] bg-[#ECFDF5]"
                    : "border-slate-200 bg-white"
                }`}
              >
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                    n.accent
                      ? "bg-demax text-white shadow-btn"
                      : n.success
                      ? "bg-[#059669] text-white"
                      : "bg-slate-50 text-demax"
                  }`}
                >
                  <n.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <span className="font-display text-[13px] font-semibold leading-tight text-ink">
                  {n.label}
                </span>
              </motion.div>

              {i < nodes.length - 1 && (
                <motion.div
                  initial={reduce ? false : { opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 + 0.2 }}
                  className="flex items-center justify-center text-slate-300"
                >
                  <ArrowRight className="h-5 w-5 rotate-90 lg:rotate-0" strokeWidth={2} />
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};
